/**
 * Text, tints and the screen-reader-only span — the three things every screen
 * was writing out longhand in an inline `style={{…}}`.
 *
 * `Txt` exists because the most common inline style in the visitor screens was
 * a span with a size, a weight and one of five greys: a hundred copies of the
 * same four properties, each free to drift from the others. The tones are the
 * token names and nothing else, so a colour that is not a token cannot be said.
 *
 * `mix()` is how a translucent version of a token is written. The pilot washes
 * a surface in its accent at a dozen alphas (`rgba(255,106,61,.12)`, `.45`…);
 * `color-mix` with `transparent` produces exactly that colour from the token,
 * so a festival whose accent is not orange gets its own wash for free and no
 * screen carries a colour literal.
 */
import type { CSSProperties, ReactNode } from 'react';

/** `mix('var(--accent)', 12)` — the token at 12% opacity. */
export function mix(color: string, percent: number): string {
  return `color-mix(in srgb, ${color} ${percent}%, transparent)`;
}

export type Tone =
  | 'txt' | 'dim' | 'muted' | 'faint' | 'accent' | 'accent2' | 'good' | 'bad' | 'on-accent' | 'inherit';

const TONE: Record<Tone, string | undefined> = {
  txt: 'var(--txt)',
  dim: 'var(--txt-dim)',
  muted: 'var(--muted)',
  faint: 'var(--faint)',
  accent: 'var(--accent)',
  accent2: 'var(--accent2)',
  good: 'var(--good)',
  bad: 'var(--bad)',
  'on-accent': 'var(--on-accent)',
  inherit: undefined,
};

type Size = number | 'badge' | 'chip' | 'body' | 'title' | 'display';
const SIZE: Record<Exclude<Size, number>, string> = {
  badge: 'var(--fs-badge)', chip: 'var(--fs-chip)', body: 'var(--fs-body)',
  title: 'var(--fs-title)', display: 'var(--fs-display)',
};

/**
 * A run of text in one of the design system's sizes and tones.
 *
 * `as="p"`/`"h2"`/`"h3"` come with `margin: 0`, because every call site here
 * was resetting it by hand.
 */
export function Txt({
  as: Tag = 'span', size, weight, tone = 'inherit', lh, track, upper, nowrap, tabular, wrap,
  ellipsis, center, style, className, children, ...rest
}: {
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'strong' | 'label';
  size?: Size; weight?: number; tone?: Tone;
  /** line-height */
  lh?: number;
  /** letter-spacing, in em */
  track?: number;
  upper?: boolean; nowrap?: boolean; tabular?: boolean;
  /** Break a long unspaced word (a name) rather than overflow. */
  wrap?: boolean;
  /** One line, cut with an ellipsis. */
  ellipsis?: boolean;
  center?: boolean;
  style?: CSSProperties;
  /** A class from a stylesheet, for what the props above do not say. */
  className?: string;
  children?: ReactNode;
  id?: string; role?: string; htmlFor?: string;
  'aria-hidden'?: boolean | 'true'; 'aria-live'?: 'polite' | 'assertive'; 'aria-label'?: string;
}) {
  const s: CSSProperties = {
    ...(size !== undefined ? { fontSize: typeof size === 'number' ? size : SIZE[size] } : null),
    ...(weight !== undefined ? { fontWeight: weight } : null),
    ...(TONE[tone] ? { color: TONE[tone] } : null),
    ...(lh !== undefined ? { lineHeight: lh } : null),
    ...(track !== undefined ? { letterSpacing: `${track}em` } : null),
    ...(upper ? { textTransform: 'uppercase' } : null),
    ...(nowrap ? { whiteSpace: 'nowrap' } : null),
    ...(tabular ? { fontVariantNumeric: 'tabular-nums' } : null),
    ...(wrap ? { wordBreak: 'break-word', minWidth: 0 } : null),
    ...(ellipsis ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 } : null),
    ...(center ? { textAlign: 'center' } : null),
    ...style,
  };
  // The margin reset is a zero-specificity class, so a class can set a margin.
  const reset = Tag === 'p' || Tag === 'h1' || Tag === 'h2' || Tag === 'h3';
  const cls = [reset && 'ui-txt-block', className].filter(Boolean).join(' ') || undefined;
  return <Tag {...rest} className={cls} style={s}>{children}</Tag>;
}

/** Read to a screen reader, invisible on screen. */
export const srOnly: CSSProperties = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

export function SrOnly({ children, ...rest }: {
  children: ReactNode; role?: string; 'aria-live'?: 'polite' | 'assertive';
}) {
  return <span {...rest} className="ui-sr-only">{children}</span>;
}
